import { useSeoMeta } from '@unhead/react';
import { TombstoneLayout } from '@/components/TombstoneLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  Play,
  Trash2,
  Eraser,
  Wifi,
  AlertTriangle,
  Shield,
  List,
  FileText,
  CheckCircle2,
  Info,
  HelpCircle,
  Globe,
  Archive,
} from 'lucide-react';

export default function DocsPage() {
  useSeoMeta({
    title: 'Documentation — Tombstone',
    description: 'Learn how to use Tombstone to manage and delete your Nostr events.',
  });

  return (
    <TombstoneLayout>
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-10">
        {/* Header */}
        <div className="space-y-3">
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight">
            Documentation
          </h1>
          <p className="text-muted-foreground max-w-2xl leading-relaxed">
            Learn how to use Tombstone to take control of your Nostr footprint.
            Understand deletion, nullification, vanish requests, and how to manage
            relay propagation across the decentralized network.
          </p>
        </div>

        {/* Main tabs */}
        <Tabs defaultValue="overview" className="space-y-8">
          <TabsList className="grid w-full grid-cols-4 h-auto p-1 bg-secondary/60 border border-border">
            <TabsTrigger
              value="overview"
              className="text-xs sm:text-sm py-2.5 data-[state=active]:bg-card data-[state=active]:text-accent data-[state=active]:border data-[state=active]:border-accent/40"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="getting-started"
              className="text-xs sm:text-sm py-2.5 data-[state=active]:bg-card data-[state=active]:text-accent data-[state=active]:border data-[state=active]:border-accent/40"
            >
              Getting Started
            </TabsTrigger>
            <TabsTrigger
              value="actions"
              className="text-xs sm:text-sm py-2.5 data-[state=active]:bg-card data-[state=active]:text-accent data-[state=active]:border data-[state=active]:border-accent/40"
            >
              Actions
            </TabsTrigger>
            <TabsTrigger
              value="relay-mgmt"
              className="text-xs sm:text-sm py-2.5 data-[state=active]:bg-card data-[state=active]:text-accent data-[state=active]:border data-[state=active]:border-accent/40"
            >
              Relay Guide
            </TabsTrigger>
          </TabsList>

          {/* ── Overview Tab ── */}
          <TabsContent value="overview" className="space-y-6 animate-fade-up">
            <div>
              <h2 className="font-display text-xl font-semibold text-foreground mb-3">
                What is Tombstone?
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Tombstone is a Nostr event management tool focused on the hardest part of
                decentralized social: <strong className="text-foreground">deletion</strong>.
                Because Nostr is permissionless and distributed, events can linger on relays
                long after you want them gone. Tombstone gives you the tools to handle that
                — properly, and with visibility into what's actually happening across your relays.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <FeatureCard
                icon={Trash2}
                title="Delete (NIP-09)"
                description="Publish cryptographically signed deletion requests that compliant relays should honor."
              />
              <FeatureCard
                icon={Eraser}
                title="Nullify"
                description="Overwrite replaceable and addressable events with empty content plus a deletion request — the most aggressive option."
              />
              <FeatureCard
                icon={Wifi}
                title="Track Propagation"
                description="See exactly which relays still carry your events after deletion requests."
              />
              <FeatureCard
                iconNode={<img src="/tombstone_skull.png" alt="" className="w-4 h-4 object-contain flex-shrink-0" />}
                title="Request to Vanish (NIP-62)"
                description="Ask specific relays — or all relays — to permanently delete everything from your public key. Legally binding in some jurisdictions."
              />
              <FeatureCard
                icon={Archive}
                title="Master Tombstone (NIP-78)"
                description="Canonical kind 30078 record aggregating all your deletions into one event. View and publish on the /master page."
              />
              <FeatureCard
                icon={Globe}
                title="Custom Discovery Relays"
                description="Scan beyond your configured relays to check propagation across well-known public relays."
              />
            </div>

            <Alert className="bg-accent/5 border-accent/20">
              <AlertTriangle className="h-4 w-4 text-accent" />
              <AlertDescription className="text-accent/90">
                <strong>Important:</strong> Nostr is a public protocol. Once an event is
                published, it can be copied. Tombstone does everything within the
                protocol's power — but it's honest about the limits.
              </AlertDescription>
            </Alert>

            <div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-3">
                The Problem Tombstone Addresses
              </h3>
              <ul className="space-y-2 text-muted-foreground list-disc pl-5">
                <li>Notes you've deleted may still be visible in some clients</li>
                <li>Articles and lists you've replaced may still exist in old form on non-compliant relays</li>
                <li>There's no standard way to know <em>which</em> relays still have your event</li>
                <li>NIP-09 deletion compliance is voluntary — some relays ignore it entirely</li>
              </ul>
            </div>
          </TabsContent>

          {/* ── Getting Started Tab ── */}
          <TabsContent value="getting-started" className="space-y-6 animate-fade-up">
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Play className="w-5 h-5 text-accent" />
                  Getting Started
                </CardTitle>
                <CardDescription>
                  Connect your Nostr identity and start managing your events
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Step number={1} title="Log in with Nostr">
                  Click the login button in the top-right corner and choose your preferred
                  Nostr signer. You can use a browser extension like nos2x, Alby, or any
                  NIP-07 compatible signer.
                </Step>

                <Step number={2} title="Scan Your Events">
                  Once logged in, Tombstone automatically scans your configured relays for
                  all supported event types. This may take a few moments depending on how
                  many events you've published.
                </Step>

                <Step number={3} title="Review Your Events">
                  Browse your events by category:
                  <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
                    <li><strong className="text-foreground">Notes (Kind 1)</strong> — Short-form social posts</li>
                    <li><strong className="text-foreground">Articles (Kind 30023/30024)</strong> — Long-form content</li>
                    <li><strong className="text-foreground">Lists &amp; Sets</strong> — Follow lists, mute lists, bookmarks, relay lists, and parameterized sets</li>
                    <li><strong className="text-foreground">Deleted</strong> — Events you've already sent deletion requests for</li>
                  </ul>
                </Step>

                <Step number={4} title="Select &amp; Take Action">
                  For any event you can:
                  <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
                    <li><strong className="text-foreground">Delete:</strong> Send a NIP-09 kind 5 deletion request</li>
                    <li><strong className="text-foreground">Nullify:</strong> Overwrite replaceable events (lists, articles, sets only)</li>
                    <li><strong className="text-foreground">Check Relay Status:</strong> Expand the card to see propagation across relays</li>
                  </ul>
                </Step>

                <Step number={5} title="Advanced: Request to Vanish">
                  For a complete erasure request, visit the{' '}
                  <a href="/master" className="text-accent hover:underline">Master Tombstone</a>{' '}
                  page and use the NIP-62 vanish controls to ask relays to delete everything
                  from your public key — including DMs.
                </Step>
              </CardContent>
            </Card>

            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Shield className="w-5 h-5 text-accent" />
                  Best Practices
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>• Always verify the event content before deleting or nullifying</p>
                <p>• Use <strong className="text-foreground">Delete</strong> first for regular notes (kind 1)</p>
                <p>• Use <strong className="text-foreground">Nullify</strong> for replaceable and addressable events — it's more thorough</p>
                <p>• Add relays you've used in the past to maximize deletion coverage</p>
                <p>• Use <strong className="text-foreground">Request to Vanish</strong> only when you want a full erasure — it cannot be reversed</p>
                <p>• Remember: deletion on Nostr is a request, not a guarantee</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Actions Tab ── */}
          <TabsContent value="actions" className="space-y-6 animate-fade-up">

            {/* Delete */}
            <ActionCard
              icon={Trash2}
              title="Delete (NIP-09)"
              subtitle="The standard way to request event deletion on Nostr"
              borderColor="border-l-accent"
            >
              <p className="text-sm text-muted-foreground leading-relaxed">
                NIP-09 defines a "deletion request" event (kind 5) that signals to
                relays and clients that an event should be removed. When you delete
                an event through Tombstone:
              </p>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal pl-4 mt-3">
                <li>A kind 5 event is signed with your key</li>
                <li>It includes both <code className="text-accent">e</code> and <code className="text-accent">a</code> tags when applicable</li>
                <li>A <code className="text-accent">k</code> tag specifies the original event kind</li>
                <li>It's published to all configured write relays</li>
                <li>Compliant relays stop serving the referenced event</li>
              </ol>
              <Alert className="bg-accent/5 border-accent/20">
                <Info className="h-4 w-4 text-accent" />
                <AlertDescription className="text-accent/90 text-xs">
                  For addressable events (kinds 30000–39999), Tombstone includes both{' '}
                  <code className="mx-1 px-1 py-0.5 bg-accent/20 rounded">e</code>
                  and
                  <code className="mx-1 px-1 py-0.5 bg-accent/20 rounded">a</code>
                  tags for maximum relay coverage.
                </AlertDescription>
              </Alert>
            </ActionCard>

            {/* Nullify */}
            <ActionCard
              icon={Eraser}
              title="Nullify"
              subtitle="Best for replaceable lists/sets and addressable events — not available for kind 1 notes"
              borderColor="border-l-accent/60"
            >
              <p className="text-sm text-muted-foreground leading-relaxed">
                For replaceable and addressable events, Tombstone publishes <strong className="text-foreground">two events</strong>:{' '}
                a new empty replacement (to overwrite content on any relay) and a NIP-09 deletion request.
              </p>
              <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-4 mt-3">
                <li>Works even if a relay ignores NIP-09</li>
                <li>Preserves the <code className="text-accent">d</code> tag for addressable events (sets, articles)</li>
                <li>Strips all other tags (title, image, description, etc.)</li>
                <li>Sets content to "Nullified by Tombstone" so the replacement is identifiable</li>
                <li>Regular notes (kind 1) cannot be nullified — Delete only</li>
              </ul>
              <Alert className="bg-accent/5 border-accent/20">
                <AlertTriangle className="h-4 w-4 text-accent" />
                <AlertDescription className="text-accent/90 text-xs">
                  The dual approach (nullification + deletion) is the most effective strategy for
                  cleaning up events on non-compliant relays.
                </AlertDescription>
              </Alert>
            </ActionCard>

            {/* Request to Vanish (NIP-62) */}
            <ActionCard
              iconNode={<img src="/tombstone_skull.png" alt="" className="w-5 h-5 object-contain flex-shrink-0" />}
              title="Request to Vanish (NIP-62)"
              subtitle="Nuclear option — asks relays to delete everything from your public key"
              borderColor="border-l-accent/40"
            >
              <p className="text-sm text-muted-foreground leading-relaxed">
                A kind 62 event requests a relay to permanently delete <em>all</em> of your
                events — not just specific ones. This includes DMs (gift wraps that p-tag your pubkey).
                It is legally binding in some jurisdictions (e.g. GDPR right to erasure).
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div className="rounded border border-border bg-secondary/20 p-3 space-y-1">
                  <p className="text-xs font-medium text-foreground">Per-Relay Vanish</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Targets specific relays. The kind 62 event is sent directly to each relay
                    with its URL in the <code className="text-accent">relay</code> tag.
                  </p>
                </div>
                <div className="rounded border border-border bg-secondary/20 p-3 space-y-1">
                  <p className="text-xs font-medium text-foreground">Global Vanish (ALL_RELAYS)</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Uses the <code className="text-accent">ALL_RELAYS</code> sentinel and broadcasts
                    to all known relays. Intended as a complete digital erasure request.
                  </p>
                </div>
              </div>
              <Alert className="bg-accent/5 border-accent/20">
                <AlertTriangle className="h-4 w-4 text-accent" />
                <AlertDescription className="text-accent/90 text-xs">
                  NIP-62 is a draft NIP. Most relays do not yet enforce it. Sending a vanish
                  request is forward-looking and legally meaningful, but current compliance
                  is limited. This action cannot be reversed.
                </AlertDescription>
              </Alert>
            </ActionCard>

            {/* Comparison Table */}
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="font-display text-sm font-medium">
                  When to Use Each Action
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto -mx-4 px-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left font-medium text-muted-foreground py-3 pr-4">Event Type</th>
                        <th className="text-left font-medium text-muted-foreground py-3 pr-3">Delete</th>
                        <th className="text-left font-medium text-muted-foreground py-3 pr-3">Nullify</th>
                        <th className="text-left font-medium text-muted-foreground py-3 pr-3">Vanish</th>
                        <th className="text-left font-medium text-muted-foreground py-3">Recommended</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                            <span>Notes (Kind 1)</span>
                          </div>
                        </td>
                        <td className="py-3 pr-3"><CheckCircle2 className="w-4 h-4 text-accent" /></td>
                        <td className="py-3 pr-3"><span className="text-muted-foreground/40">—</span></td>
                        <td className="py-3 pr-3"><CheckCircle2 className="w-4 h-4 text-accent/50" /></td>
                        <td className="py-3"><span className="text-accent font-medium text-xs">Delete</span></td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                            <span>Articles (30023/30024)</span>
                          </div>
                        </td>
                        <td className="py-3 pr-3"><CheckCircle2 className="w-4 h-4 text-accent" /></td>
                        <td className="py-3 pr-3"><CheckCircle2 className="w-4 h-4 text-accent" /></td>
                        <td className="py-3 pr-3"><CheckCircle2 className="w-4 h-4 text-accent/50" /></td>
                        <td className="py-3"><span className="text-accent font-medium text-xs">Nullify (best)</span></td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <List className="w-3.5 h-3.5 text-muted-foreground" />
                            <span>Lists &amp; Sets</span>
                          </div>
                        </td>
                        <td className="py-3 pr-3"><CheckCircle2 className="w-4 h-4 text-accent" /></td>
                        <td className="py-3 pr-3"><CheckCircle2 className="w-4 h-4 text-accent" /></td>
                        <td className="py-3 pr-3"><CheckCircle2 className="w-4 h-4 text-accent/50" /></td>
                        <td className="py-3"><span className="text-accent font-medium text-xs">Nullify</span></td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                            <span>Everything (full erasure)</span>
                          </div>
                        </td>
                        <td className="py-3 pr-3"><span className="text-muted-foreground/40">—</span></td>
                        <td className="py-3 pr-3"><span className="text-muted-foreground/40">—</span></td>
                        <td className="py-3 pr-3"><CheckCircle2 className="w-4 h-4 text-accent" /></td>
                        <td className="py-3"><span className="text-destructive font-medium text-xs">Vanish (NIP-62)</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Relay Guide Tab ── */}
          <TabsContent value="relay-mgmt" className="space-y-6 animate-fade-up">
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-accent" />
                  Why Relay Management Matters
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p className="leading-relaxed">
                  Not all relays honor NIP-09 deletion requests. To maximize efficacy, add
                  all relays where your events may have propagated — including relays
                  you've used in the past. Tombstone will broadcast your deletion request
                  to every configured relay with write access.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-border bg-secondary/20 p-4">
                    <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-accent" />
                      Your Relays (NIP-65)
                    </h4>
                    <p className="text-sm leading-relaxed">
                      Used for both discovery and broadcasting deletion requests.
                      Configured on the Relays page. Changes are published as a NIP-65 event.
                    </p>
                  </div>
                  <div className="rounded-lg border border-border bg-secondary/20 p-4">
                    <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5 text-accent" />
                      Custom Discovery Relays
                    </h4>
                    <p className="text-sm leading-relaxed">
                      Extra public relays used only for checking event propagation.
                      Does not affect your NIP-65 list or synced profile.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="font-display text-sm font-medium">Propagation Status Indicators</CardTitle>
                <CardDescription>
                  How to interpret the relay status shown in each event card
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <StatusIndicator
                  dotColor="bg-accent"
                  label="Found"
                  description="Event still exists on this relay — deletion not yet honored"
                />
                <StatusIndicator
                  dotColor="bg-muted-foreground"
                  label="Absent"
                  description="Relay does not have the event — deletion was likely honored"
                />
                <StatusIndicator
                  dotColor="bg-muted-foreground/40"
                  label="Timeout / Error"
                  description="Relay did not respond in time or the connection failed"
                />
                <Alert className="mt-4 bg-accent/5 border-accent/20">
                  <HelpCircle className="h-4 w-4 text-accent" />
                  <AlertDescription className="text-accent/90 text-sm">
                    Click "Details" inside any event card, then toggle "Scan wider" to also
                    check popular public relays and your custom discovery relays.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="font-display text-sm font-medium">NIP-65 Relay List</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-muted-foreground">
                <p className="leading-relaxed">
                  When you're logged in, changes to your relay configuration are
                  automatically published as a NIP-65 relay list event. This ensures
                  your relay preferences are synced across all Nostr clients you use.
                </p>
                <p className="text-sm">
                  Visit the{' '}
                  <a href="/relays" className="text-accent hover:underline">Relays page</a>{' '}
                  to manage your list, test connectivity, and set read/write permissions.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ── FAQ ── */}
        <div className="border-t border-border pt-8 space-y-6">
          <h2 className="font-display text-xl font-semibold text-foreground">
            Frequently Asked Questions
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <FAQQuestion
              question="Can I guarantee deletion?"
              answer="No. Nostr is permissionless — once published, anyone can copy an event. Tombstone does everything within the protocol's power to request deletion, but non-compliant relays may retain your data."
            />
            <FAQQuestion
              question="What's the difference between Delete and Nullify?"
              answer="Delete sends a NIP-09 kind 5 request. Nullify publishes both an empty replacement (to overwrite content on non-compliant relays) and a deletion request. Nullify is only available for replaceable lists/sets and addressable events — regular notes (kind 1) show Delete only."
            />
            <FAQQuestion
              question="What is a Request to Vanish?"
              answer="NIP-62 defines a kind 62 event that asks a relay to delete everything from your public key — all events, DMs, and all future re-broadcasts. It's a draft NIP and most relays don't yet support it, but it's legally meaningful in some jurisdictions."
            />
            <FAQQuestion
              question="Why do I still see deleted events?"
              answer="Common reasons: (1) The relay doesn't honor NIP-09, (2) The event was cached on a relay you're not connected to, (3) The client has stale data, or (4) Someone reposted the content before deletion."
            />
            <FAQQuestion
              question="Which event types are supported?"
              answer="Short notes (kind 1), long-form articles (30023/30024), follow lists (3), mute lists (10000), pinned notes (10001), relay lists (10002), bookmarks (10003), follow sets (30000), bookmark sets (30003), and curation sets (30004)."
            />
            <FAQQuestion
              question="Is there a recovery option?"
              answer="No. Once you delete, nullify, or vanish events, the action is final. Always double-check the content before proceeding."
            />
            <FAQQuestion
              question="Can I bulk delete events?"
              answer="Not yet. Events must be handled one at a time to prevent accidental mass deletion. Bulk operations may be added in the future with proper safeguards."
            />
            <FAQQuestion
              question="What is the Master Tombstone?"
              answer="A kind 30078 (NIP-78) event with d='tombstone.master' that aggregates all your deletion references into one canonical record. It does not replace individual kind 5 events — it supplements them as a record of your deletion history."
            />
          </div>
        </div>

        {/* Footer note */}
        <div className="text-center text-xs text-muted-foreground pt-4">
          <p>
            Need more help? Visit the{' '}
            <a href="/relays" className="text-accent hover:underline">Relays page</a>{' '}
            to configure your connection and test relay status.
          </p>
        </div>
      </div>
    </TombstoneLayout>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function FeatureCard({
  icon: Icon,
  iconNode,
  title,
  description,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  iconNode?: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-2">
      <div className="flex items-center gap-2">
        {iconNode ?? (Icon ? <Icon className="w-4 h-4 text-accent" /> : null)}
        <h3 className="font-medium text-foreground text-sm">{title}</h3>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

function ActionCard({
  icon: Icon,
  iconNode,
  title,
  subtitle,
  borderColor,
  children,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  iconNode?: React.ReactNode;
  title: string;
  subtitle: string;
  borderColor: string;
  children: React.ReactNode;
}) {
  return (
    <Card className={`border-l-4 ${borderColor} border-border bg-card`}>
      <CardHeader className="pb-3">
        <CardTitle className="font-display flex items-center gap-2 text-foreground text-base">
          {iconNode ?? (Icon ? <Icon className="w-5 h-5 text-accent" /> : null)}
          {title}
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          {subtitle}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {children}
      </CardContent>
    </Card>
  );
}

function Step({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0">
        <div className="w-8 h-8 rounded-full bg-accent/15 text-accent flex items-center justify-center font-semibold text-sm border border-accent/30">
          {number}
        </div>
      </div>
      <div className="space-y-2 pb-2">
        <h4 className="font-medium text-foreground">{title}</h4>
        <div className="text-sm text-muted-foreground leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function StatusIndicator({
  dotColor,
  label,
  description,
}: {
  dotColor: string;
  label: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-secondary/10">
      <span className={`w-2 h-2 rounded-full ${dotColor} mt-1.5 flex-shrink-0`} />
      <div>
        <p className="font-medium text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function FAQQuestion({ question, answer }: { question: string; answer: string }) {
  return (
    <div className="rounded-lg border border-border bg-card/50 p-4 space-y-2">
      <h4 className="font-medium text-foreground text-sm flex items-center gap-2">
        <HelpCircle className="w-3.5 h-3.5 text-accent flex-shrink-0" />
        {question}
      </h4>
      <p className="text-xs text-muted-foreground leading-relaxed">{answer}</p>
    </div>
  );
}
